import { displayValue, fieldSpec, leafKey } from './field-specs';
import type {
  FieldPresentation,
  SelectChoice,
  SettingsCardSpec,
  SettingsValues,
} from './types';

export type LeafLabel = (leaf: string) => string;

/** Values only the running app knows, and the copy each level words its own way. */
export interface GroupSpecOptions {
  /**
   * Models across providers (`llmModelChoices`). Each choice `sets` its provider leaf, so a card
   * given only these shows no Provider field; without them both stay plain fields.
   */
  models?: SelectChoice[];
  /**
   * The LLM providers this deployment offers. Given these, the card shows a Provider select
   * beside the models and lists only the chosen provider's models. Choosing a provider also
   * sets its recommended or first listed model, '' when it lists none.
   */
  providers?: SelectChoice[];
  /**
   * Copy for `<leafKey>.tooltip`, `<leafKey>.placeholder`, `<leafKey>.help`,
   * `<group>.help` and `decision_config_provider.<value>` (an option's
   * description). Undefined leaves that part out.
   */
  text?: (id: string) => string | undefined;
  /** The voice catalog's choices; without them the voice cards keep plain fields. */
  voice?: VoiceChoices;
  /** Whether the resolved speech model is Deepgram Flux; undefined when unknown. */
  flux?: boolean;
  /** A number of days as the app words it: "30 days", "1 year". */
  days?: (days: number) => string;
}

/** Built by the app from its voice catalog. */
export interface VoiceChoices {
  /** Each `sets` its provider's default speech model. */
  sttProviders: SelectChoice[];
  /** Each listed `when` its provider is chosen. */
  sttModels: SelectChoice[];
  /** Each `sets` its TTS provider, so the card needs no Provider field. */
  voices: SelectChoice[];
  /** Each listed `when` its TTS provider is chosen. */
  ttsModels: SelectChoice[];
}

export type GroupSpecFactory = (
  label: LeafLabel,
  options?: GroupSpecOptions
) => SettingsCardSpec;

type Text = NonNullable<GroupSpecOptions['text']>;
const noText: Text = () => undefined;

/** The unit a leaf's number is in, read from its name: `_ms`, `_seconds`. */
export const leafUnit = (leaf: string): string | undefined =>
  leaf.endsWith('_ms') ? 'ms' : leaf.endsWith('_seconds') ? 's' : undefined;

const isWide = (leaf: string) =>
  ['toggle', 'list', 'json'].includes(fieldSpec(leaf).kind);

const present = (
  leaf: string,
  label: LeafLabel,
  text: Text
): FieldPresentation => {
  const copy = (part: string) => text(`${leafKey(leaf)}.${part}`);
  return {
    leaf,
    label: copy('label') ?? label(leaf),
    unit: leafUnit(leaf),
    tooltip: copy('tooltip'),
    sublabel: copy('sublabel'),
    placeholder: copy('placeholder'),
    help: copy('help'),
    span: isWide(leaf) ? 2 : 1,
  };
};

const card =
  (leaves: string[]): GroupSpecFactory =>
  (label, { text = noText } = {}) => ({
    fields: leaves.map((leaf) => present(leaf, label, text)),
  });

const PROVIDER = 'model_config.provider';
const MODEL = 'model_config.model';

const select = (options: SelectChoice[]) =>
  ({ kind: 'select', options }) as const;

// The design has no Provider field: the vendor follows the model. Without a
// model list both stay, or the provider could not change.
const llm: GroupSpecFactory = (
  label,
  { models, providers, text = noText } = {}
) => {
  const field = (leaf: string) => present(leaf, label, text);
  const provider = (choice: SelectChoice) => choice.sets?.[PROVIDER];
  // A switch must not keep the old provider's model, or the pair saves invalid.
  const firstModel = (id: string) => {
    const own = models?.filter((m) => provider(m) === id) ?? [];
    return (own.find((m) => m.recommended) ?? own[0])?.value ?? '';
  };
  const showsProvider = !models || Boolean(providers);
  return {
    fill: (values) => {
      const model = firstModel(displayValue(values[PROVIDER]));
      return model && displayValue(values[MODEL]) === ''
        ? { [MODEL]: model }
        : undefined;
    },
    fields: [
      ...(!showsProvider
        ? []
        : [
            {
              ...field(PROVIDER),
              ...(providers && {
                control: select(
                  providers.map((p) => ({
                    ...p,
                    sets: { [MODEL]: firstModel(p.value) },
                  }))
                ),
              }),
            },
          ]),
      {
        ...field(MODEL),
        // A blank model drops from the save and resolves from the parent, maybe another vendor's.
        ...(showsProvider && {
          required: {
            when: (values: SettingsValues) =>
              displayValue(values[PROVIDER]) !== '',
            message: text('model_config_model.required'),
          },
        }),
        ...(models && {
          control: select(
            providers
              ? models.map((m) => ({ ...m, when: { [PROVIDER]: provider(m) } }))
              : models
          ),
          span: providers ? 1 : 2,
        }),
      },
      field('model_config.temperature'),
      field('model_config.max_tokens'),
    ],
  };
};

/** Decision-model backends in the design's order; their names are product names. */
export const DECISION_PROVIDERS = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'typesafe', label: 'TypeSafe' },
  { value: 'ollaya', label: 'Ollaya' },
] as const;

const decisionModel: GroupSpecFactory = (label, { text = noText } = {}) => {
  const field = (leaf: string) => present(leaf, label, text);
  return {
    fields: [
      {
        ...field('decision_config.provider'),
        control: {
          kind: 'select',
          options: DECISION_PROVIDERS.map(({ value, label: name }, index) => ({
            value,
            label: name,
            description: text(`decision_config_provider.${value}`),
            recommended: index === 0,
          })),
        },
      },
      field('decision_config.model'),
      field('decision_config.endpoint'),
      field('decision_config.timeout_ms'),
    ],
    help: text('decision_model.help'),
  };
};

const voice = (leaves: string[]) => leaves.map((l) => `channels.voice.${l}`);
const V = (leaf: string) => `channels.voice.${leaf}`;

// Only the multilingual Flux model hears more than one language; flux-general-en is English only.
const MULTILINGUAL_MODEL = 'flux-general-multi';

// The design has no key field: the STT key secret keeps its value, unseen.
const stt: GroupSpecFactory = (
  label,
  { voice: choices, text = noText } = {}
) => {
  const field = (leaf: string) => present(leaf, label, text);
  return {
    fields: [
      {
        ...field(V('stt.provider')),
        ...(choices && { control: select(choices.sttProviders) }),
      },
      {
        ...field(V('stt.model')),
        ...(choices && { control: select(choices.sttModels) }),
        helpWhen: (values) => values[V('stt.model')] === MULTILINGUAL_MODEL,
      },
    ],
  };
};

const slider = (min: number, max: number) =>
  ({ kind: 'slider', min, max, step: 0.05 }) as const;

// One voice row: the backend holds a single language's voice, so there is no
// language list or Add Language until it holds one per language.
const languages: GroupSpecFactory = (
  label,
  { voice: choices, text = noText } = {}
) => {
  const field = (leaf: string) => present(leaf, label, text);
  const voiceName = (values: SettingsValues) => {
    const id = values[V('tts.voice_id')];
    return (
      choices?.voices.find((v) => v.value === id)?.label ?? String(id ?? '')
    );
  };
  return {
    fields: [
      ...(choices ? [] : [field(V('tts.provider'))]),
      {
        ...field(V('tts.model')),
        ...(choices && { control: select(choices.ttsModels) }),
      },
      {
        ...field(V('tts.voice_id')),
        ...(choices && { control: select(choices.voices) }),
      },
      { ...field(V('tts.speed')), control: slider(0.7, 1.2), span: 2 },
      { ...field(V('tts.stability')), control: slider(0, 1), span: 2 },
      { ...field(V('tts.similarity_boost')), control: slider(0, 1), span: 2 },
    ],
    ...(choices && {
      panel: {
        title: voiceName,
        meta: (values) => {
          const model = values[V('tts.model')];
          return typeof model === 'string' ? model : undefined;
        },
      },
    }),
  };
};

// Flux ends turns itself, so it reads the threshold and ignores the silence
// buffer (endpointing); other models are the reverse. Unknown shows both.
const conversation: GroupSpecFactory = (
  label,
  { flux, text = noText } = {}
) => {
  const field = (leaf: string) => present(leaf, label, text);
  const buffer = field(V('stt.endpointing_ms'));
  const locked = text('channels_voice_stt_endpointing_ms.locked');
  return {
    fields: [
      ...(flux === false
        ? []
        : [
            {
              ...field(V('stt.eot_threshold')),
              control: slider(0, 1),
              span: 2 as const,
            },
          ]),
      flux && locked
        ? {
            ...buffer,
            span: 2,
            locked: {
              display: locked,
              badge: text('channels_voice_stt_endpointing_ms.lockedBadge'),
            },
          }
        : { ...buffer, span: 2 },
      {
        ...field(V('silence.max_consecutive')),
        control: { kind: 'slider', min: 1, max: 5, step: 1, off: 0 },
        span: 2,
      },
    ],
  };
};

// The design's presets. The backend takes any positive number of days, so a
// stored value outside them stays selectable, named by `days`.
const RETENTION_PRESETS: Array<[string, number[]]> = [
  ['privacy.recording_days', [7, 30, 90, 365]],
  ['privacy.transcript_days', [30, 90, 365]],
];

const retention: GroupSpecFactory = (
  label,
  { days = String, text = noText } = {}
) => ({
  layout: 'rows',
  fields: RETENTION_PRESETS.map(([leaf, presets]) => ({
    ...present(leaf, label, text),
    control: {
      kind: 'select',
      options: presets.map((n) => ({ value: String(n), label: days(n) })),
      otherLabel: (value) =>
        Number.isFinite(Number(value)) ? days(Number(value)) : value,
    },
    span: 2,
  })),
});

// Leaves and order mirror maestro-platform's Settings.Groups; a group's design
// (pickers, tooltips, which leaves show) replaces its entry when its page is rebuilt.
export const GROUP_SPECS: Record<string, GroupSpecFactory> = {
  llm,
  decision_model: decisionModel,
  stt,
  languages,
  conversation,
  recording: card(voice(['twilio.recording_enabled'])),
  retention,
};

/** The card spec for `group`, or undefined to render every leaf as the backend lists it. */
export const groupSpec = (
  group: string,
  label: LeafLabel,
  options?: GroupSpecOptions
): SettingsCardSpec | undefined => GROUP_SPECS[group]?.(label, options);
